import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { isProfileComplete } from '@/lib/profile';
import type { AppRole } from '@/types/supabase';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

export function RequireRole({
  allow,
  children,
}: {
  allow: readonly AppRole[];
  children: ReactNode;
}) {
  const { initializing: authLoading } = useAuth();
  const { data: profile, isLoading } = useProfile();

  if (authLoading || isLoading) return <FullPageLoading />;
  if (!profile) return <Navigate to="/login" replace />;

  if (profile.status === 'pending') return <Navigate to="/pending" replace />;
  if (profile.status === 'suspended' || profile.status === 'archived') {
    return <Navigate to="/access-denied" replace />;
  }
  if (!allow.includes(profile.role)) return <Navigate to="/access-denied" replace />;
  return <>{children}</>;
}

// Layout route: redirects to /portal/complete-profile if profile is incomplete.
export function RequireCompleteProfile() {
  const { data: profile, isLoading } = useProfile();
  const { initializing } = useAuth();
  if (initializing || isLoading) return <FullPageLoading />;
  if (!isProfileComplete(profile)) return <Navigate to="/portal/complete-profile" replace />;
  return <Outlet />;
}

export function RoleRouter() {
  const { initializing } = useAuth();
  const { data: profile, isLoading } = useProfile();
  if (initializing || isLoading) return <FullPageLoading />;
  if (!profile) return <Navigate to="/login" replace />;
  if (profile.status === 'pending') return <Navigate to="/pending" replace />;
  if (profile.role === 'staff') return <Navigate to="/staff" replace />;
  return <Navigate to="/portal" replace />;
}

function FullPageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      …
    </div>
  );
}
